import React, { useMemo } from 'react';
import { compose, sortBy, toLower, prop, map, reduce, propEq, find, append, filter, propOr } from 'ramda';
import { propNotEq } from 'ramda-adjunct';
import { RoleLabelEnum, RoleValueEnum, ARCHIVED_LABEL_SUFFIX } from '../components/users/utils/roles';
import { ROLES, TEAM_ROLES } from '../lib/rights';

const useUserFilterOptions = (teams, offers, isAdministration) => {
  const options = useMemo(() => {
    const mapTeamsToOptions = map(team => ({
      label: team['name'],
      value: team['id']
    }));

    const groupOffersByName = reduce((acc, { id, offerName, isArchived }) => {
      const existing = find(propEq(offerName, 'label'), acc);
      if (existing) {
        // If an option with the same offerName already exists, append the id to its value
        existing.value = `${prop('value', existing)},${id}`;
      } else {
        acc = append({
          label: offerName, value: id, isArchived 
        }, acc);
      }
      return acc;
    }, []);

    const optionsByItems = isAdministration
      ? mapTeamsToOptions(teams)
      : groupOffersByName(offers);

    const sortedOptions = sortBy(compose(toLower, prop('label')))(optionsByItems);
    if (isAdministration) {
      return [
        {
          label: RoleLabelEnum.WITHOUT_TEAMS,
          value: RoleValueEnum.EMPTY
        },
        ...sortedOptions
      ];
    }

    return sortedOptions;
  }, [teams, offers, isAdministration]);

  const optionsToDisplay = useMemo(() => map((option) => {
    if (isAdministration) return option;
    const isArchived = propOr(false, 'isArchived', option);
    const label = prop('label', option);
    return {
      ...option,
      label: isArchived ? `${label} ${ARCHIVED_LABEL_SUFFIX}` : label
    };
  }, options), [options, isAdministration]);

  const roles = useMemo(() => {
    const baseRoles = isAdministration ? TEAM_ROLES : filter(propNotEq(ROLES.superAdmin, 'value'), TEAM_ROLES);
    
    const additionalRoles = [];
    if (isAdministration) additionalRoles.push({ label: RoleLabelEnum.WITHOUT_ROLES, value: RoleValueEnum.EMPTY });
    return [...baseRoles, ...additionalRoles];
  }, [isAdministration]);

  return {
    options,
    optionsToDisplay,
    roles
  };
};

export default useUserFilterOptions;